// ADR 11.9.26i §2 — the name a browser gives itself.
//
// On the phones "anonymous" is an identity: a server-signed device token,
// minted by a proof-of-work solve, renewed in the background, metered by the
// backend as a subject. On the web it was the *absence* of one. This module
// mints the same token the same way, with the three departures the ADR names:
//
//   • **Token or nothing** (§1). No legacy `X-Device-ID`, so there is nothing
//     to send *while* enrolling — which forces the next departure.
//   • **Awaited, not fire-and-forget** (§2). The phones' first request goes out
//     on the legacy header and must not wait on a solve (7.8.26c D5). The
//     browser has no header to send meanwhile, so the request that needs an
//     identity awaits `ensureDeviceToken()`. The surface that needs it starts
//     the solve on mount, so it overlaps the visitor's level choice; and
//     `device_enrolled.solve_ms` is the number that bet is graded on.
//   • **A failure is visible** (§2). 7.8.26c D6 could degrade every loss path
//     because the legacy header was always there to send; here there is no
//     degraded request to make, so a failed enrolment rejects and the caller
//     fails through `api/http.ts`'s ordinary error path.
//
// Single-flighted by one module-level promise, `tokenStore.ts`'s refresh shape:
// two components that both ask produce one challenge and one register (AC2).
// The promise is cleared on settle, so a failed enrolment is retried by the
// next caller rather than cached as a rejection.
//
// The solve reaches the worker through `credentials.ts::solveChallenge` and
// nothing else — this file imports nothing from `../wasm` (AC3). 5.9.26p's
// reason is unchanged by the search now being wasm: a synchronous multi-second
// loop freezes the tab whichever language it is compiled from.
//
// **The import graph has a cycle here, and it is safe.** `api/device.ts` and
// `credentials.ts` both reach `api/http.ts`, and `http.ts` reaches back through
// `api/deviceReplay.ts` for the replay-once arm (§5). Every reference across
// the cycle is inside a function body — nothing is read at module evaluation —
// so the ESM live bindings resolve by the time any of them is called.

import { deviceChallenge, registerDevice } from '../api/device'
import { deviceEnrolled } from '../features/vocab/analytics'
import { solveChallenge } from './credentials'
import {
  clearDeviceToken,
  deviceTokenLifetime,
  nowUnix,
  readDeviceToken,
  writeDeviceToken,
} from './deviceStore'

let inFlight: Promise<string> | null = null

/**
 * Challenge → solve → register, and adopt the token the server minted.
 *
 * `outgoing` is the token being renewed, or `null` on a first enrolment. A
 * renewal that forgot it would mint a fresh subject and hand the visitor a new
 * day (7.8.26c D2/D3). `attest` on the challenge is read and ignored — a
 * browser has nothing to attest with, and the field is `Option` on the wire
 * for that case.
 */
async function enrol(outgoing: string | null): Promise<string> {
  const { nonce, difficulty } = await deviceChallenge()
  const solveStartedAt = performance.now()
  const solution = await solveChallenge({ nonce, difficulty })
  const solveMs = Math.round(performance.now() - solveStartedAt)
  const { device_token } = await registerDevice({ nonce, solution, outgoing_token: outgoing })
  writeDeviceToken(device_token)
  deviceEnrolled({ difficulty, solveMs, renewal: outgoing !== null })
  return device_token
}

/** One enrolment at a time; the next caller after a settle starts a fresh one. */
function enrolOnce(outgoing: string | null): Promise<string> {
  if (inFlight === null) {
    inFlight = enrol(outgoing).finally(() => {
      inFlight = null
    })
  }
  return inFlight
}

/**
 * The device token to send now, enrolling first when there is none.
 *
 *     stored, not due  → the stored token
 *     stored, due      → the stored token now; a background renewal carries it
 *                        as `outgoing_token` and replaces it on arrival (§4)
 *     none, or expired → enrol, and resolve the new token
 *
 * The renewal's failure is swallowed on purpose: the stored token is still
 * good for up to thirty days, and the next caller inside the window tries
 * again. A first enrolment's failure is not — see the module header.
 */
export async function ensureDeviceToken(): Promise<string> {
  const stored = readDeviceToken()
  if (stored !== null) {
    // ADR 25.9.26f §3 — async now: the lifetime question reaches the wasm
    // through a dynamic import, so `http.ts` stays off the facade.
    const lifetime = await deviceTokenLifetime(stored, nowUnix())
    if (lifetime === 'live') return stored
    if (lifetime === 'due') {
      enrolOnce(stored).catch(() => {})
      return stored
    }
    // Expired is treated as none — dropped here rather than sent and refused,
    // so no request goes out on a token this side already knows is dead.
    clearDeviceToken()
  }
  return enrolOnce(null)
}

/** Test seam: forget an in-flight enrolment between cases. */
export function resetDeviceEnrolment(): void {
  inFlight = null
}
